import { useParams } from "react-router-dom";
import useCollaborators from "datasets/hooks/useCollaborators";
import useDataset from "datasets/hooks/useDataset";
import usePreviewMode from "datasets/hooks/usePreviewMode";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import type { DatasetGroup } from "datasets/types/interfaces/collaborators";
import {
  type AccessLevelOption,
  type ShareGroup,
  type ShareUser,
} from "~/pages/manifold-nextgen/components/share/types";
import { apiMutate, getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

export const useDatasetShare = () => {
  const { datasetId } = useParams();
  const {
    data: rawCollaborators,
    isLoading,
    error: collaboratorsError,
    mutate,
  } = useCollaborators();
  const collaborators = rawCollaborators
    ? {
        users: rawCollaborators.users.map((u) => ({
          email: u.email,
          full_name: u.full_name,
          image_url: u.image_url,
          access_level: u.access_level as string | null,
        })),
        groups: rawCollaborators.groups.map((g) => ({
          name: g.name,
          display_name: g.display_name,
          is_internal: g.is_internal,
          access_level: g.access_level as string | null,
          users: g.users.map((u) => ({
            email: u.email,
            full_name: u.full_name,
            image_url: u.image_url,
            access_level: u.access_level as string | null,
          })),
        })),
      }
    : undefined;
  const { data: dataset } = useDataset();
  const previewFeatureEnabled = usePreviewMode();
  const showPreview = previewFeatureEnabled && (dataset?.previewEnabled ?? false);

  const accessLevels: Record<string, AccessLevelOption> = {
    [DatasetAccessLevel.OWNER]: {
      label: "Owner",
      description: "Full access with ability to modify data assets",
    },
    [DatasetAccessLevel.ANALYZE]: {
      label: "Analyze",
      description: "Read-only access to all data assets",
    },
    ...(showPreview && {
      [DatasetAccessLevel.PREVIEW]: {
        label: "Preview",
        description: "Read-only access to limited data assets",
      },
    }),
  };

  const defaultAccessLevel = showPreview ? DatasetAccessLevel.PREVIEW : DatasetAccessLevel.ANALYZE;

  const handleShare = async (
    recipient: { user?: ShareUser; group?: ShareGroup },
    accessLevel: string | null
  ) => {
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${datasetId}/share`,
        "POST",
        {
          ...(recipient.user && { email: recipient.user.email }),
          ...(recipient.group && { group: recipient.group.name }),
          access_level: accessLevel,
        },
        accessLevel === null ? { expects204: true } : { expectsText: true }
      );
    } catch (e) {
      throw new Error(isApiError(e) ? getApiErrorMessage(e) : "Unable to update dataset access");
    }

    trackEvent("dataset_access_shared", {
      dataset_id: datasetId,
      ...(recipient.user && { collaborator_email: recipient.user.email }),
      ...(recipient.group && { collaborator_group: recipient.group.name }),
      access_level: accessLevel ?? "removed",
      action: accessLevel === null ? "remove_access" : "grant_access",
    });

    if (recipient.user) {
      mutate(
        (data) => {
          if (!data) return data;
          if (accessLevel === null) {
            return { ...data, users: data.users.filter((u) => u.email !== recipient.user!.email) };
          }
          const idx = data.users.findIndex((u) => u.email === recipient.user!.email);
          const updated = [...data.users];
          if (idx >= 0) {
            updated[idx] = { ...updated[idx], access_level: accessLevel as DatasetAccessLevel };
          } else {
            updated.push({ ...recipient.user!, access_level: accessLevel as DatasetAccessLevel });
          }
          return { ...data, users: updated };
        },
        { revalidate: true }
      );
    }

    if (recipient.group) {
      mutate(
        (data) => {
          if (!data) return data;
          if (accessLevel === null) {
            return {
              ...data,
              groups: data.groups.filter((g) => g.name !== recipient.group!.name),
            };
          }
          const idx = data.groups.findIndex((g) => g.name === recipient.group!.name);
          const updated = [...data.groups];
          if (idx >= 0) {
            updated[idx] = { ...updated[idx], access_level: accessLevel as DatasetAccessLevel };
          } else {
            updated.push({
              ...recipient.group!,
              access_level: accessLevel as DatasetAccessLevel,
            } as DatasetGroup);
          }
          return { ...data, groups: updated };
        },
        { revalidate: true }
      );
    }
  };

  return {
    collaborators,
    isLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  };
};
